import { useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ClipboardList, Video, BookOpen, Check, Clock, ExternalLink, Users, LogOut,
  Target, X, TrendingUp, ListTodo, CalendarClock, CalendarDays, ChevronRight,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/EmptyState';
import { useAssignments } from '../hooks/useAssignments';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { useConfirm } from '../hooks/useConfirm';
import { getAssignmentUrl, formatTurkishDueDate } from '../utils/assignment';
import { getClass3DIconPath, classInitials, getClassGradient } from '../utils/classIcon';
import type { ClassRow, ClassStudentRow, LiveSessionRow } from '../types/sinif';

interface JoinedClassInfo extends ClassRow {
  studentStatus: ClassStudentRow['status'];
}



function formatDueLabel(dueDate: string) {
  const due = new Date(dueDate);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(due) - startOfDay(now)) / 86400000);
  const time = due.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return `Bugün, ${time}`;
  if (diffDays === 1) return `Yarın, ${time}`;
  if (diffDays < 0) return `${due.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} (gecikti)`;
  if (diffDays < 7) return `${due.toLocaleDateString('tr-TR', { weekday: 'long' })}, ${time}`;
  return `${due.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}, ${time}`;
}

// Sınıfları görsel olarak ayırt etmek için ton döngüsü — mevcut semantic
// token'lardan (accent/success/warning) + kod tabanında zaten kullanılan purple
// tonundan türetilmiş, banner gradyanı + rozet + ilerleme çubuğu için.
const CLASS_TONES = [
  { gradient: 'from-accent-500 to-accent-600', bar: 'bg-accent-500', dot: 'bg-accent-500', badgeLight: 'bg-accent-100 dark:bg-accent-500/15 text-accent-700 dark:text-accent-400' },
  { gradient: 'from-success-500 to-emerald-600', bar: 'bg-success-500', dot: 'bg-success-500', badgeLight: 'bg-success-100 dark:bg-success-500/15 text-success-700 dark:text-success-400' },
  { gradient: 'from-purple-500 to-indigo-600', bar: 'bg-purple-500', dot: 'bg-purple-500', badgeLight: 'bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400' },
  { gradient: 'from-warning-500 to-orange-600', bar: 'bg-warning-500', dot: 'bg-warning-500', badgeLight: 'bg-warning-100 dark:bg-warning-500/15 text-warning-900 dark:text-warning-400' },
] as const;

export default function StudentAssignments() {
  const { user } = useUser();
  const getSupabase = useSupabaseClient();
  const { assignments, isLoading, markCompleted, refresh } = useAssignments();
  const { confirm, ConfirmDialog } = useConfirm();

  const [joinedClasses, setJoinedClasses] = useState<JoinedClassInfo[]>([]);
  const [teacherNames, setTeacherNames] = useState<Record<string, string>>({});
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [filterMode, setFilterMode] = useState<'all' | 'pending' | 'completed' | 'week'>('pending');
  const [activeMeetSessions, setActiveMeetSessions] = useState<LiveSessionRow[]>([]);
  const [dismissedMeetIds, setDismissedMeetIds] = useState<Set<string>>(new Set());
  const assignmentsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadData() {
      if (!user) return;
      try {
        const supabase = await getSupabase();
        const { data: memberRows } = await supabase
          .from('class_students')
          .select('class_id, status')
          .eq('student_id', user.id)
          .in('status', ['aktif', 'bekliyor']);

        const members = (memberRows as Pick<ClassStudentRow, 'class_id' | 'status'>[]) ?? [];
        const classIds = members.map(m => m.class_id);

        if (classIds.length > 0) {
          const { data: classRows } = await supabase
            .from('classes')
            .select('*')
            .in('id', classIds)
            .eq('is_archived', false);

          const statusMap = new Map(members.map(m => [m.class_id, m.status]));
          const list: JoinedClassInfo[] = (classRows as ClassRow[] ?? []).map(c => ({
            ...c,
            studentStatus: statusMap.get(c.id) ?? 'aktif',
          }));
          setJoinedClasses(list);

          // Sınıfları kuran öğretmenlerin isimlerini profil veritabanından toplu çek
          const teacherIds = Array.from(new Set(list.map(c => c.teacher_id).filter(Boolean)));
          if (teacherIds.length > 0) {
            const { data: profRows } = await supabase
              .from('profiles')
              .select('user_id, full_name')
              .in('user_id', teacherIds);

            const map: Record<string, string> = {};
            (profRows as { user_id: string; full_name: string | null }[] ?? []).forEach(p => {
              if (p.full_name) map[p.user_id] = p.full_name;
            });
            setTeacherNames(map);
          }

          // Canlı dersleri çek (öğrencinin sınıfları veya doğrudan kendisine olanlar)
          const { data: liveRows } = await supabase
            .from('live_sessions')
            .select('*')
            .eq('status', 'planlandi')
            .or(`student_id.eq.${user.id},class_id.in.(${classIds.join(',')})`)
            .order('created_at', { ascending: false });

          setActiveMeetSessions((liveRows as LiveSessionRow[]) ?? []);
        } else {
          setJoinedClasses([]);
          setActiveMeetSessions([]);
        }
      } catch (err) {
        console.error('Veriler alınamadı:', err);
      } finally {
        setLoadingClasses(false);
      }
    }
    loadData();
  }, [user, getSupabase]);

  const classToneMap = useMemo(() => {
    const map = new Map<string, (typeof CLASS_TONES)[number]>();
    joinedClasses.forEach((c, i) => map.set(c.id, CLASS_TONES[i % CLASS_TONES.length]));
    return map;
  }, [joinedClasses]);

  async function leaveClass(classId: string, className: string) {
    if (!user) return;
    const ok = await confirm({
      title: 'Sınıftan ayrıl',
      message: `"${className}" sınıfından ayrılmak istediğine emin misin?`,
      confirmLabel: 'Ayrıl',
      danger: true,
    });
    if (!ok) return;
    try {
      const supabase = await getSupabase();
      await supabase
        .from('class_students')
        .update({ status: 'ayrildi', left_at: new Date().toISOString() })
        .eq('class_id', classId)
        .eq('student_id', user.id);

      setJoinedClasses(prev => prev.filter(c => c.id !== classId));
      refresh();
    } catch (err) {
      console.error('Sınıftan ayrılamadı:', err);
    }
  }

  const pending = assignments.filter(a => a.status !== 'tamamlandi');
  const completed = assignments.filter(a => a.status === 'tamamlandi');
  const completionRatio = assignments.length > 0 ? Math.round((completed.length / assignments.length) * 100) : 0;

  const displayedAssignments = assignments.filter(a => {
    if (filterMode === 'pending') return a.status !== 'tamamlandi';
    if (filterMode === 'completed') return a.status === 'tamamlandi';
    if (filterMode === 'week') {
      if (a.status === 'tamamlandi' || !a.due_date) return false;
      const diffMs = new Date(a.due_date).getTime() - Date.now();
      return diffMs > -86400000 && diffMs < 7 * 86400000;
    }
    return true;
  });

  const upcoming = useMemo(() => {
    return pending
      .filter(a => a.due_date)
      .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
      .slice(0, 5);
  }, [pending]);

  function openWeeklyCalendar() {
    setFilterMode('week');
    assignmentsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-6xl mx-auto">
      <Helmet>
        <title>Görevlerim | Bitmiyo Sınıf</title>
        <meta name="description" content="Öğretmenleriniz tarafından atanan ödevler ve katıldığınız sınıflar." />
      </Helmet>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Karşılama Kartı */}
          <Card className="p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
              <div>
                <p className="text-text-secondary text-sm mb-1">Merhaba, {user?.firstName ?? 'Öğrenci'}</p>
                <h1 className="text-2xl font-black text-text-primary" style={{ fontFamily: 'var(--font-heading)' }}>
                  {pending.length > 0 ? `${pending.length} bekleyen görevin var` : 'Bekleyen görevin yok'}
                </h1>
              </div>

              {/* Filtre sekmesi */}
              {assignments.length > 0 && (
                <div className="flex items-center bg-surface border border-border rounded-xl p-1 text-xs font-bold w-full sm:w-auto overflow-x-auto no-scrollbar">
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${filterMode === 'all' ? 'bg-accent-500 text-white shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                  >
                    Tümü ({assignments.length})
                  </button>
                  <button
                    onClick={() => setFilterMode('pending')}
                    className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${filterMode === 'pending' ? 'bg-accent-500 text-white shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                  >
                    Bekleyen ({pending.length})
                  </button>
                  <button
                    onClick={() => setFilterMode('completed')}
                    className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${filterMode === 'completed' ? 'bg-success-500 text-white shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                  >
                    Tamamlanan ({completed.length})
                  </button>
                </div>
              )}
            </div>

            {assignments.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-black uppercase tracking-widest text-text-secondary flex items-center gap-1.5">
                    <TrendingUp size={13} className="text-success-500" /> Genel İlerleme
                  </span>
                  <span className="text-xs font-black text-text-primary">%{completionRatio}</span>
                </div>
                <div className="h-2 rounded-full bg-border overflow-hidden">
                  <div
                    className="h-full rounded-full bg-success-500 transition-all duration-500"
                    style={{ width: `${completionRatio}%` }}
                  />
                </div>
              </div>
            )}
          </Card>

          {/* Özet İstatistik Kartları */}
          {assignments.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              <Card className="p-4 text-center">
                <p className="text-2xl font-black text-text-primary" style={{ fontFamily: 'var(--font-heading)' }}>
                  {assignments.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5 uppercase tracking-wide">Toplam Görev</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-2xl font-black text-warning-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  {pending.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5 uppercase tracking-wide">Bekleyen</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-2xl font-black text-success-500" style={{ fontFamily: 'var(--font-heading)' }}>
                  {completed.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5 uppercase tracking-wide">Tamamlanan</p>
              </Card>
            </div>
          )}

          {/* Google Meet Canlı Ders Bildirim Banner'ı */}
          {activeMeetSessions.filter(s => !dismissedMeetIds.has(s.id)).length > 0 && (
            <div className="flex flex-col gap-3">
              {activeMeetSessions.filter(s => !dismissedMeetIds.has(s.id)).map(session => (
                <Card key={session.id} className="p-5 border-blue-300 dark:border-blue-500/30 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white shadow-lg relative overflow-hidden">
                  <button
                    onClick={() => setDismissedMeetIds(prev => new Set(prev).add(session.id))}
                    className="absolute top-2 right-2 p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                    title="Bildirimi Kapat"
                  >
                    <X size={16} />
                  </button>
                  <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-white/10" />
                  <div className="relative flex items-center justify-between gap-4 flex-wrap pr-6">
                    <div className="flex items-center gap-3">
                      <Video size={32} className="text-white shrink-0 animate-bounce drop-shadow-md" />
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                          <p className="font-black text-sm uppercase tracking-wider text-white">CANLI DERS YAYINDA!</p>
                        </div>
                        <p className="text-xs text-white/80">Öğretmeniniz Google Meet canlı ders oturumu başlattı.</p>
                      </div>
                    </div>

                    <a
                      href={session.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-blue-700 hover:bg-blue-50 font-black text-xs px-4 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-1.5 shrink-0"
                    >
                      <ExternalLink size={14} /> Google Meet ile Derse Katıl
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Kayıtlı Olduğun Sınıflar */}
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-2">
              <Users size={16} className="text-accent-500" /> Kayıtlı Sınıflarım
            </h2>

            {loadingClasses && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[0, 1].map(i => (
                  <Card key={i} className="overflow-hidden">
                    <div className="h-20 animate-shimmer" />
                    <div className="p-4 flex flex-col gap-2">
                      <div className="h-3.5 w-2/3 rounded-md animate-shimmer" />
                      <div className="h-2.5 w-1/3 rounded-md animate-shimmer" />
                      <div className="h-1.5 w-full rounded-full animate-shimmer mt-2" />
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {!loadingClasses && joinedClasses.length === 0 && (
              <EmptyState
                icon={Users}
                title="Henüz hiçbir sınıfa kayıtlı değilsin"
                description="Öğretmeninden aldığın katılım kodunu Sınıfa Katıl sekmesinden gir."
              />
            )}

            {!loadingClasses && joinedClasses.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {joinedClasses.map((c, idx) => {
                  const classAssignments = assignments.filter(a => a.class_id === c.id);
                  const done = classAssignments.filter(a => a.status === 'tamamlandi').length;
                  const pendingCount = classAssignments.length - done;
                  const ratio = classAssignments.length > 0 ? Math.round((done / classAssignments.length) * 100) : 100;
                  const gradient = getClassGradient(c.id);

                  return (
                    <Link
                      key={c.id}
                      to={`/sinif/${c.id}`}
                      className="group relative overflow-hidden rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 animate-fade-in-up block"
                      style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                    >
                      {/* Gradient arka plan */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
                      {/* Dekoratif daireler */}
                      <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10" />
                      <div className="absolute -bottom-4 -right-2 w-20 h-20 rounded-full bg-white/10" />

                      <div className="relative p-5">
                        <div className="flex items-center justify-between mb-3">
                          {/* 3D İkon Logo / Harf Avatar */}
                          {getClass3DIconPath(c) ? (
                            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm p-1.5 flex items-center justify-center border border-white/30 shadow-md">
                              <img src={getClass3DIconPath(c)!} alt={c.name} className="w-full h-full object-contain filter drop-shadow" />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center font-black text-white text-lg border border-white/20 shadow-md">
                              {classInitials(c.name)}
                            </div>
                          )}

                          <button
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              leaveClass(c.id, c.name);
                            }}
                            className="p-2 rounded-xl bg-white/20 hover:bg-white/30 text-white/80 hover:text-white backdrop-blur-sm transition-all"
                            title="Sınıftan Ayrıl"
                          >
                            <LogOut size={15} />
                          </button>
                        </div>

                        <p className="font-black text-white text-xl leading-tight mb-1 truncate" style={{ fontFamily: 'var(--font-heading)' }}>
                          {c.name}
                        </p>
                        <p className="text-xs text-white/80 font-medium truncate mb-4">
                          {teacherNames[c.teacher_id] ?? 'Öğretmen'}
                        </p>

                        <div className="flex items-center justify-between pt-3 border-t border-white/20">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-white/20 text-white backdrop-blur-sm">
                              {pendingCount > 0 ? `${pendingCount} ödev var` : 'Tamamlandı'}
                            </span>
                            {c.studentStatus === 'bekliyor' && (
                              <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/80 text-white">
                                Onay Bekliyor
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-1 text-white font-bold text-xs">
                            <span>İlerleme: %{ratio}</span>
                            <ChevronRight size={16} className="text-white/70 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {isLoading && (
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map(i => (
                <Card key={i} className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-border animate-shimmer shrink-0" />
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="h-3.5 w-2/3 rounded-md animate-shimmer" />
                    <div className="h-2.5 w-1/3 rounded-md animate-shimmer" />
                  </div>
                </Card>
              ))}
            </div>
          )}

          {!isLoading && assignments.length === 0 && (
            <EmptyState
              icon={ClipboardList}
              title="Henüz görev yok"
              description="Öğretmenin bir görev atadığında burada görünecek."
            />
          )}

          {/* Görev Listesi */}
          {!isLoading && assignments.length > 0 && (
            <div ref={assignmentsListRef}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-black uppercase tracking-widest text-text-secondary flex items-center gap-2">
                  <ListTodo size={16} className="text-accent-500" /> Görevlerim{filterMode === 'week' ? ' · Bu Hafta' : ''}
                </h2>
                {filterMode === 'week' && (
                  <button onClick={() => setFilterMode('all')} className="text-xs font-bold text-accent-600 dark:text-accent-500 hover:underline">
                    Filtreyi Temizle
                  </button>
                )}
              </div>

              {displayedAssignments.length === 0 ? (
                <Card className="p-6 text-center text-sm text-text-muted font-medium">
                  {filterMode === 'pending'
                    ? ' Harika! Bekleyen veya gecikmiş görevin bulunmuyor.'
                    : filterMode === 'completed'
                    ? 'Henüz tamamlanan bir görevin yok.'
                    : 'Bu aralıkta görev bulunmuyor.'}
                </Card>
              ) : (
                <div className="flex flex-col gap-3">
                  {displayedAssignments.map((a, i) => {
                    const isDone = a.status === 'tamamlandi';
                    const targetUrl = getAssignmentUrl(a.type, a.resource_ref);

                    return (
                      <Card
                        key={a.id}
                        className={`p-4 flex items-center gap-4 transition-all animate-fade-in-up ${
                          isDone ? 'opacity-60 bg-surface/50' : 'hover:border-accent-300'
                        }`}
                        style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                      >
                        {/* Tip ikonu */}
                        <div className={`shrink-0 ${
                          isDone
                            ? 'text-success-500'
                            : a.type === 'video'
                            ? 'text-accent-600 dark:text-accent-500'
                            : a.type === 'test'
                            ? 'text-purple-600 dark:text-purple-400'
                            : 'text-text-secondary'
                        }`}>
                          {isDone ? <Check size={24} /> : a.type === 'video' ? <Video size={24} /> : a.type === 'test' ? <Target size={24} /> : <BookOpen size={24} />}
                        </div>

                        {/* İçerik */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-bold text-sm truncate ${isDone ? 'line-through text-text-muted' : 'text-text-primary'}`}>
                              {a.title}
                            </p>
                            {isDone && <Badge variant="success">Tamamlandı</Badge>}
                            {a.type === 'test' && !isDone && <Badge variant="accent">Test Ödevi</Badge>}
                          </div>

                          {a.due_date && (
                            <div className="flex items-center gap-1 text-text-muted text-xs mt-0.5">
                              <Clock size={11} />
                              Teslim: {formatTurkishDueDate(a.due_date)}
                            </div>
                          )}

                          {targetUrl && (
                            <a
                              href={targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-xs mt-1.5 inline-flex items-center gap-1 font-bold px-2.5 py-1 rounded-lg transition-all border ${
                                a.type === 'test'
                                  ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-500/20 hover:bg-purple-500/20'
                                  : 'bg-accent-500/10 text-accent-600 dark:text-accent-400 border-accent-200 dark:border-accent-500/20 hover:bg-accent-500/20'
                              }`}
                              onClick={e => e.stopPropagation()}
                            >
                              <ExternalLink size={12} />
                              {a.type === 'test' ? 'Testi Çöz (soru.bitmiyo.com.tr)' : a.type === 'video' ? 'Videoyu İzle' : 'Bağlantıyı Aç'}
                            </a>
                          )}
                        </div>

                        {/* Tamamlandı butonu */}
                        {!isDone && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markCompleted(a.id)}
                            className="shrink-0"
                          >
                            <Check size={14} /> Tamamlandı
                          </Button>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Yaklaşan Ödevler Sidebar */}
        <aside className="lg:sticky lg:top-6">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-black uppercase tracking-widest text-text-secondary flex items-center gap-2">
                <CalendarClock size={16} className="text-accent-500" /> Yaklaşan Ödevler
              </h2>
            </div>

            {upcoming.length === 0 ? (
              <p className="text-xs text-text-muted text-center py-6">Yaklaşan ödevin yok.</p>
            ) : (
              <div className="flex flex-col gap-4 mb-4">
                {upcoming.map(a => {
                  const cls = joinedClasses.find(c => c.id === a.class_id);
                  const tone = cls ? classToneMap.get(cls.id) : undefined;
                  return (
                    <div key={a.id} className="flex items-start gap-2.5">
                      <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${tone?.dot ?? 'bg-accent-500'}`} />
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-text-muted truncate">{cls?.name ?? 'Sınıf'}</p>
                        <p className="text-sm font-bold text-text-primary truncate">{a.title}</p>
                        <p className="text-[11px] text-text-muted flex items-center gap-1 mt-0.5">
                          <Clock size={10} /> {formatDueLabel(a.due_date!)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Button variant="outline" size="sm" className="w-full" onClick={openWeeklyCalendar}>
              <CalendarDays size={14} /> Haftalık Takvimi Aç
            </Button>
          </Card>
        </aside>
      </div>

      {ConfirmDialog}
    </div>
  );
}
